/**
 * Vista de la tabla de amortización con presentación según `estado_tabla` del crédito:
 *
 *   - "proyeccion" (semanal/quincenal/mensual antes del desembolso):
 *        Lista SIMPLE — solo #, fecha estimada, pago total. Banner amarillo
 *        "Proyección estimada — sujeta a condiciones finales del crédito".
 *
 *   - "diaria" (créditos de periodicidad diaria, en cualquier etapa):
 *        Lista SIMPLE de pagos — sin desglose contable, sin PDF.
 *        Banner neutro "Plan de pagos diarios".
 *
 *   - "definitiva" (semanal/quincenal/mensual post-desembolso):
 *        Tabla COMPLETA con desglose (# / fecha / capital / interés / IVA / pago / saldo).
 *        Banner verde "Tabla de amortización definitiva".
 *        Botón "Descargar PDF" que llama al endpoint que genera el PDF con
 *        membrete de la SOFOM (sin marca Felvora).
 *
 * El componente NO recalcula nada — todo lo lee del crédito ya persistido.
 */
import { useState } from "react";
import { AlertTriangle, Download, FileText, Info, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { mxn, fdate, formatearPlazo } from "@/lib/format";
import { esCreditoFactoraje, periodicidadCredito, plazoCredito, PERIODICIDAD_FACTORAJE } from "@/lib/factoraje";
import { TablaFactoraje } from "@/components/creditos/TablaFactoraje";
import { TablaArrendamiento } from "@/components/creditos/TablaArrendamiento";
import { esCreditoArrendamiento, separarResidual } from "@/lib/arrendamiento";
import { toast } from "sonner";

/**
 * Banner informativo de "Gastos de cobranza aplicables en caso de mora".
 *
 * REGLAS DE NEGOCIO (auditoría feb-2026):
 *  - Los gastos de cobranza NO se cobran pago a pago ni se suman a la cuota regular.
 *  - Son un dato INFORMATIVO de transparencia al cliente. Solo aplican si el crédito
 *    entra en mora (bucket > 0 días de atraso), y en ese caso se cargan por gestión.
 *  - No modifican ningún cálculo de la tabla, del motor de mora ni de las pólizas.
 *  - Si el crédito no trae valor (0 / vacío) => NO se pinta el banner (limpieza visual).
 */
function BannerGastosCobranza({ credito }) {
  const val = Number(credito?.gastos_cobranza || 0);
  if (!Number.isFinite(val) || val <= 0) return null;
  const tipo = String(credito?.gastos_cobranza_tipo || "porcentaje").toLowerCase();
  const monto = tipo === "monto" ? mxn(val) : `${val}%`;
  const gcModo = String(credito?.gc_modo || "manual").toLowerCase();
  const gcEtiqueta = credito?.gc_etiqueta || "Gastos de cobranza";
  const disparo = Math.max(1, Number(credito?.gc_disparo_dias || 1));
  const esAutomatico = gcModo === "automatico";
  return (
    <div data-testid="tabla-banner-gastos-cobranza"
      className="mb-2 flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      {esAutomatico ? (
        <div>
          <p className="font-semibold">
            {gcEtiqueta} aplicable en caso de mora:{" "}
            <span data-testid="gastos-cobranza-valor" className="font-mono">{monto}</span>
          </p>
          <p>
            Dato informativo de transparencia. <b>No</b> forma parte de las cuotas regulares;
            se aplicaría automáticamente a partir del día <b>{disparo}</b> de atraso.
            Este cargo es <b>adicional</b> al interés moratorio y causa IVA.
          </p>
        </div>
      ) : (
        <div>
          <p className="font-semibold">
            Gastos de cobranza aplicables en caso de mora:{" "}
            <span data-testid="gastos-cobranza-valor" className="font-mono">{monto}</span>
          </p>
          <p>
            Dato informativo de transparencia. <b>No</b> forman parte de las cuotas regulares;
            únicamente se aplicarían si el crédito cae en atraso, mediante gestión de cobranza.
          </p>
        </div>
      )}
    </div>
  );
}

function BannerProyeccion({ titulo = "" }) {
  return (
    <div data-testid="tabla-banner-proyeccion"
      className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-semibold">Proyección estimada{titulo ? ` — ${titulo}` : ""}</p>
        <p>Sujeta a condiciones finales del crédito. La tabla definitiva se genera y sella al momento del desembolso.</p>
      </div>
    </div>
  );
}

function BannerDiaria() {
  return (
    <div data-testid="tabla-banner-diaria"
      className="mb-2 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
      <FileText className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-semibold">Plan de pagos diarios</p>
        <p>Los créditos de periodicidad diaria operan con lista simple de pagos. No genera tabla de amortización formal ni PDF descargable.</p>
      </div>
    </div>
  );
}

function BannerDefinitiva({ credito }) {
  const fs = credito?.fecha_sellado;
  const plazoTxt = esCreditoFactoraje(credito) ? plazoCredito(credito)
    : credito?.plazo ? formatearPlazo(credito.plazo, credito.periodicidad) : "";
  return (
    <div data-testid="tabla-banner-definitiva"
      className="mb-2 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p className="font-semibold">Tabla de amortización definitiva{plazoTxt ? ` — ${plazoTxt}` : ""}</p>
        <p>Sellada al desembolso{fs ? ` el ${String(fs).slice(0, 19).replace("T", " ")}` : ""}. Rige pagos, cobranza y contabilidad.</p>
      </div>
    </div>
  );
}

function TablaSimple({ tabla, dataTestid }) {
  if (!tabla || tabla.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500" data-testid={`${dataTestid}-empty`}>
        Sin plan de pagos disponible.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white" data-testid={dataTestid}>
      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-50">
            <tr className="text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Fecha estimada</th>
              <th className="px-3 py-2 text-right">Total del periodo</th>
            </tr>
          </thead>
          <tbody>
            {tabla.map((r) => (
              <tr key={r.no} className="border-t border-slate-100 tabular-nums" data-testid={`${dataTestid}-fila-${r.no}`}>
                <td className="px-3 py-2 text-slate-500">
                  {r.no}
                  {r.es_proporcional && (
                    <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700"
                      title={`Primer pago proporcional por ${r.dias_proporcional || "los"} días entre el desembolso y la primera fecha ancla`}>
                      Proporcional{r.dias_proporcional ? ` · ${r.dias_proporcional} días` : ""}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600">{fdate(r.fecha_pago)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{mxn(r.pago)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TablaCompleta({ tabla, dataTestid, resaltarTotal = false }) {
  if (!tabla || tabla.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500" data-testid={`${dataTestid}-empty`}>
        Sin tabla de amortización.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white" data-testid={dataTestid}>
      <div className="max-h-[520px] overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-50">
            <tr className="text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2 text-right">Capital</th>
              <th className="px-3 py-2 text-right">Interés</th>
              <th className="px-3 py-2 text-right">IVA</th>
              <th className="px-3 py-2 text-right" title="Capital + interés — constante en sistema francés">Cuota</th>
              <th className="px-3 py-2 text-right" title="Cuota + IVA del periodo">Total</th>
              <th className="px-3 py-2 text-right">Saldo</th>
              <th className="px-3 py-2 text-right">Estatus</th>
            </tr>
          </thead>
          <tbody>
            {tabla.map((c) => (
              <tr key={c.no} className="border-t border-slate-100 tabular-nums" data-testid={`${dataTestid}-fila-${c.no}`}>
                <td className="px-3 py-2 text-slate-500">
                  {c.no}
                  {c.es_proporcional && (
                    <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700"
                      title={`Primer pago proporcional por ${c.dias_proporcional || "los"} días entre el desembolso y la primera fecha ancla`}>
                      Proporcional{c.dias_proporcional ? ` · ${c.dias_proporcional} días` : ""}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-500">{fdate(c.fecha_pago)}</td>
                <td className="px-3 py-2 text-right font-mono">{mxn(c.capital)}</td>
                <td className="px-3 py-2 text-right font-mono">{mxn(c.interes)}</td>
                <td className="px-3 py-2 text-right font-mono">{mxn(c.iva_interes || c.iva)}</td>
                <td className={`px-3 py-2 text-right font-mono${resaltarTotal ? "" : " font-semibold"}`}>{mxn(c.cuota_credito ?? Math.round((Number(c.capital || 0) + Number(c.interes || 0)) * 100) / 100)}</td>
                <td className={`px-3 py-2 text-right font-mono${resaltarTotal ? " font-semibold" : ""}`}>{mxn(c.total_periodo ?? c.pago)}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-500">{mxn(c.saldo_insoluto)}</td>
                <td className="px-3 py-2 text-right text-xs">
                  <span className={
                    c.estatus === "Pagada" ? "text-emerald-600" :
                    c.estatus === "Vencida" ? "text-red-600" :
                    c.estatus === "Parcial" ? "text-amber-600" : "text-slate-500"
                  }>{c.estatus || "Pendiente"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TablaAmortizacionView({ credito }) {
  const [dl, setDl] = useState(false);

  if (!credito) return null;

  const estado = credito.estado_tabla || "proyeccion";
  const periodicidad = credito.periodicidad;
  const tabla = credito.tabla_amortizacion || [];
  const esFx = esCreditoFactoraje(credito);
  const esArr = esCreditoArrendamiento(credito);
  const nPagos = esArr ? separarResidual(tabla).rentas.length : tabla.length;

  const descargarPdf = async () => {
    setDl(true);
    try {
      const res = await api.get(`/creditos/${credito.id}/tabla-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tabla-amortizacion-${credito.numero_credito || credito.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDl(false);
    }
  };

  const descargarAcuse = async () => {
    setDl(true);
    try {
      const res = await api.get(`/creditos/${credito.id}/acuse-desembolso-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `acuse-desembolso-${credito.numero_credito || credito.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDl(false);
    }
  };

  return (
    <div data-testid={`tabla-amort-view-${estado}`}>
      {estado === "proyeccion" && (
        <>
          <BannerProyeccion titulo={esFx ? PERIODICIDAD_FACTORAJE : ""} />
          <BannerGastosCobranza credito={credito} />
          {esFx ? <TablaFactoraje tabla={tabla} dataTestid="tabla-factoraje-proyeccion" />
            : esArr ? <TablaArrendamiento tabla={tabla} dataTestid="tabla-arr-proyeccion" conEstatus={false} />
            : <TablaSimple tabla={tabla} dataTestid="tabla-simple-proyeccion" />}
        </>
      )}
      {estado === "diaria" && (
        <>
          <BannerDiaria />
          <BannerGastosCobranza credito={credito} />
          <TablaSimple tabla={tabla} dataTestid="tabla-simple-diaria" />
        </>
      )}
      {estado === "definitiva" && (
        <>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <BannerDefinitiva credito={credito} />
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button data-testid="descargar-acuse-desembolso-btn" size="sm" variant="outline"
                onClick={descargarAcuse} disabled={dl}>
                {dl ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
                Acuse de desembolso
              </Button>
              <Button data-testid="descargar-tabla-pdf-btn" size="sm" variant="outline"
                onClick={descargarPdf} disabled={dl || !tabla.length}>
                {dl ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
                Descargar PDF
              </Button>
            </div>
          </div>
          <BannerGastosCobranza credito={credito} />
          {esFx ? <TablaFactoraje tabla={tabla} dataTestid="tabla-factoraje-definitiva" />
            : esArr ? <TablaArrendamiento tabla={tabla} dataTestid="tabla-arr-definitiva" />
            : <TablaCompleta tabla={tabla} dataTestid="tabla-completa-definitiva"
              resaltarTotal={credito.tipo_amortizacion === "francesa_total_fijo"} />}
        </>
      )}
      {estado && !["proyeccion", "diaria", "definitiva"].includes(estado) && (
        // Compat legacy — muestra tabla completa sin banner
        <TablaCompleta tabla={tabla} dataTestid="tabla-completa-legacy"
          resaltarTotal={credito.tipo_amortizacion === "francesa_total_fijo"} />
      )}
      {tabla.length > 0 && ["definitiva", "proyeccion", "diaria"].includes(estado) && (
        <p className="mt-2 text-[11px] text-slate-400">
          {nPagos} {esArr ? "renta" : "pago"}{nPagos === 1 ? "" : "s"} — periodicidad{" "}
          <span className="font-medium" data-testid="tabla-periodicidad">{(esFx ? periodicidadCredito(credito) : periodicidad) || "—"}</span>
          {credito.cat != null && estado === "definitiva" && (
            <> · CAT referencial: <span className="font-medium">{credito.cat}%</span></>
          )}
        </p>
      )}
    </div>
  );
}

export default TablaAmortizacionView;
